// src/components/admin/AdminStatusPage.tsx
// Admin status board — engineering tasks + marketing campaigns

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import BackgroundShapes from '../common/BackgroundShapes';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

interface TaskItem {
  slug: string;
  title: string;
  created: string;
  status: string;
}

interface PlatformItem {
  platform: string;
  status: string;
}

interface Campaign {
  name: string;
  platforms: PlatformItem[];
}

interface StatusData {
  engineering: {
    inProgress: string;
    nextSteps: string;
    handoffSources: string[];
    isHandoffStale: boolean;
    recentlyCompleted: TaskItem[];
    allCompleted: TaskItem[];
    backlog: TaskItem[];
  };
  marketing: {
    campaigns: Campaign[];
  };
  lastUpdated: string;
  isStale: boolean;
}

const AdminStatusPage: React.FC = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<StatusData | null>(null);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE}/admin/status`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!res.ok) {
          throw new Error(`Request failed: ${res.status}`);
        }
        const json = await res.json();
        if (!json.success) throw new Error(json.message || 'Failed to load status');
        setData(json.data);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load status';
        setError(message);
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  const handleBackToDashboard = () => {
    navigate('/admin/dashboard');
  };

  const formatLastUpdated = (iso: string) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  /** Render a block of text — if lines start with `-` render as bullet list, else pre. */
  const renderTextBlock = (text: string) => {
    if (!text) return <p className="text-coffee opacity-60 text-sm italic">None</p>;
    const lines = text.split('\n').filter(l => l.trim() !== '');
    const allBullets = lines.every(l => l.trim().startsWith('-') || l.trim().startsWith('*') || /^\d+\./.test(l.trim()));
    if (allBullets) {
      return (
        <ul className="list-disc list-inside space-y-1 text-coffee text-sm">
          {lines.map((line, i) => (
            <li key={i}>{line.trim().replace(/^[-*]\s*/, '').replace(/^\d+\.\s*/, '')}</li>
          ))}
        </ul>
      );
    }
    return (
      <pre className="text-coffee text-sm whitespace-pre-wrap font-sans leading-relaxed">{text}</pre>
    );
  };

  return (
    <div className="min-h-screen bg-paper relative">
      <BackgroundShapes />

      <div className="container mx-auto px-4 py-8 relative z-10">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-start gap-4 mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={handleBackToDashboard}
              className="bg-cream border-2 border-coffee text-coffee px-3 py-2 rounded-lg hover:bg-white transition-colors shadow-sm text-sm font-medium"
            >
              ← Dashboard
            </button>
            <div>
              <h1 className="text-3xl font-bold text-coffee mb-1">Status Board</h1>
              {data && (
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-coffee opacity-70 text-sm">
                    Last updated: {formatLastUpdated(data.lastUpdated)}
                  </p>
                  {data.isStale && (
                    <span className="bg-amber-100 text-amber-800 border border-amber-300 rounded px-2 py-0.5 text-xs font-medium">
                      ⚠️ Stale
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="bg-white border-2 border-coffee text-coffee px-4 py-2 rounded-lg hover:bg-cream transition-colors shadow-sm"
          >
            Sign Out
          </button>
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12 text-coffee">Loading status...</div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6 text-red-800">
            {error}
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* Engineering Section */}
            <section className="mb-10">
              <h2 className="text-2xl font-bold text-coffee mb-4">Engineering</h2>

              {/* In Progress — full width */}
              <div className="bg-cream border-2 border-coffee rounded-xl p-5 shadow-[4px_4px_0px_#594A42] mb-6">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="text-lg font-bold text-coffee">🔄 In Progress</h3>
                  {data.engineering.isHandoffStale && (
                    <span className="bg-amber-100 text-amber-800 border border-amber-300 rounded px-2 py-0.5 text-xs font-medium">
                      ⚠️ handoff may be stale
                    </span>
                  )}
                </div>
                {data.engineering.handoffSources.length > 0 && (
                  <p className="text-coffee opacity-50 text-xs mb-3 italic">
                    from {data.engineering.handoffSources.join(', ')}
                  </p>
                )}
                {renderTextBlock(data.engineering.inProgress)}
              </div>

              {/* Next Steps + Backlog | Recently Done */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Next Steps with Backlog appended */}
                <div className="bg-cream border-2 border-coffee rounded-xl p-5 shadow-sm">
                  <h3 className="text-lg font-bold text-coffee mb-1">⏭️ Next</h3>
                  {data.engineering.handoffSources.length > 0 && (
                    <p className="text-coffee opacity-50 text-xs mb-3 italic">
                      from {data.engineering.handoffSources.join(', ')}
                    </p>
                  )}
                  {renderTextBlock(data.engineering.nextSteps)}

                  {data.engineering.backlog.length > 0 && (
                    <div className="mt-5 pt-4 border-t border-coffee/20">
                      <h4 className="text-sm font-bold text-coffee mb-2">📋 Coming Up</h4>
                      <ul className="space-y-1.5">
                        {data.engineering.backlog.map(task => (
                          <li key={task.slug} className="text-sm text-coffee">
                            {task.title}
                            <span className="ml-2 text-coffee opacity-50 text-xs">{task.created}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Recently Done */}
                <div className="bg-cream border-2 border-coffee rounded-xl p-5 shadow-sm">
                  <h3 className="text-lg font-bold text-coffee mb-3">✅ Recently Done</h3>
                  {data.engineering.recentlyCompleted.length > 0 ? (
                    <ul className="space-y-1.5">
                      {data.engineering.recentlyCompleted.map(task => (
                        <li key={task.slug} className="text-sm text-coffee">
                          {task.title}
                          <span className="ml-2 text-coffee opacity-50 text-xs">{task.created}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-coffee opacity-60 text-sm italic">None in last 30 days</p>
                  )}
                </div>
              </div>
            </section>

            {/* Marketing Section */}
            <section>
              <h2 className="text-2xl font-bold text-coffee mb-4">Marketing</h2>
              {data.marketing.campaigns.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {data.marketing.campaigns.map(campaign => (
                    <div
                      key={campaign.name}
                      className="bg-cream border-2 border-coffee rounded-xl p-5 shadow-sm"
                    >
                      <h3 className="text-lg font-bold text-coffee mb-3">🚀 {campaign.name}</h3>
                      {campaign.platforms.length > 0 ? (
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-coffee/20">
                              <th className="text-left text-coffee font-semibold pb-1">Platform</th>
                              <th className="text-left text-coffee font-semibold pb-1 pl-4">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {campaign.platforms.map((p, i) => (
                              <tr key={i} className="border-b border-coffee/10 last:border-0">
                                <td className="py-1.5 text-coffee font-medium">{p.platform}</td>
                                <td className="py-1.5 text-coffee opacity-80 pl-4">{p.status}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <p className="text-coffee opacity-60 text-sm italic">No platform data</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-cream border-2 border-coffee rounded-xl p-5 shadow-sm">
                  <p className="text-coffee opacity-60 text-sm italic">No campaign docs found</p>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminStatusPage;
